import { getRelatedDocuments } from '@/features/city-clerk/admin-check/api/admin-documents-api'
import { getDownloadPresignedUrl } from '@/features/city-clerk/file/api/attachment-api'
import type { GetPresignedDownloadUrlResponse } from '@/features/city-clerk/file/types/attachment-types'
import { CommentType } from '@/types/pdf-viewer'
import type {
  Comment,
  CommentReply,
  CommentReplyRequest,
  CommentsResponse,
  MeasurementExportData,
  OverlaysResponse,
  Replies,
} from '@/types/pdf-viewer'
import type {
  GetMeasurementsApiResponse,
  BackendComment,
  CityClerkCommentRequest,
  KeynotesMatchingResponse,
  AiChecklistCategoryDto,
  AiChecklistEvidenceBbox,
  BackendQcCheckFindingDto,
  QcEvidenceBboxDto,
} from '../types/playground-api-types'
import {
  normalizeCommentsResponse,
  normalizeMeasurementExportData,
  normalizeKeynotesMatchingResponse,
  backendKeynotesV2ToApiResponse,
} from './normalizers'
import { api } from '@/lib/axios-client'
import { queryClient } from '@/lib/query-client'
import { queryKeys } from '@/lib/query-keys'
import type { QcOverlaysDto } from '@/features/city-clerk/new-playground/types/qc-overlays-types'
import {
  filterLayersWithZeroCoordinates,
  normalizeLayersFromQcFindings,
  normalizeQcBbox,
} from './helpers/api-helpers'
import { AI_STATUS_ACCENT, normalizeAiStatus } from './helpers/qc-status'
import { usePdfDocStore } from '@/stores/usePdfDocStore'

export type PlaygroundSourceDocument = {
  requiredDocument: string
  attachmentId: string
  fileName: string
  url: string
  fileSize: number
  kind: string | null
}

export type DocumentScaleConfigResponse = {
  id: string
  text: string
  value: number
  attachmentId: string
}

export async function getPlaygroundSourceDocumentsByPermitSubmittalId(
  permitSubmitalId: string,
): Promise<PlaygroundSourceDocument[]> {
  if (!permitSubmitalId || permitSubmitalId === 'default') {
    return []
  }

  const related = await getRelatedDocuments(permitSubmitalId)
  if (!related || related.length === 0) {
    return []
  }

  const withAttachments = related.filter(
    (doc) => doc.attachmentId != null && doc.attachmentId !== '',
  )

  if (withAttachments.length === 0) {
    return []
  }

  const resolved = await Promise.all(
    withAttachments.map(async (doc) => {
      try {
        const presigned = await getDownloadPresignedUrl(doc.attachmentId!)
        const payload: GetPresignedDownloadUrlResponse = presigned!
        return {
          requiredDocument: doc.requiredDocument,
          attachmentId: doc.attachmentId!,
          fileName: payload?.fileName ?? doc.fileName,
          fileSize: payload?.fileSize ?? 0,
          url: payload?.presignedUrl ?? '',
          kind: doc.kind ?? null,
        } satisfies PlaygroundSourceDocument
      } catch {
        return null
      }
    }),
  )

  return resolved.filter((d): d is PlaygroundSourceDocument => d != null)
}

// qc_api's extracted drawing scale for an attachment, one entry per sheet.
// `value` is the machine-readable scale (80 for a sheet drawn at 1" = 80');
// `text` is the label verbatim off the drawing, typographic quotes included.
type QcDrawingScalesResponse = {
  attachmentId: string
  requestId: string | null
  scales: Array<{
    pageNumber: number
    value: number
    unit: string
    text: string
    documentType: string
  }>
}

// The AI-detected scale, used to seed the measurement store on mount so the
// measure tools aren't disabled (BottomToolbar's isMeasurementDisabled is
// driven by scaleFactor, so a 0 here greys them out permanently).
//
// Reads /QcFindings/DrawingScales, which serves what qpoint-core's
// extract_scale persisted to recognition.drawing_scale, resolved through the
// attachment's most recent qc_api request.
//
// The legacy /SitePlanJobs/Scales below reads the public.scale table, which
// the recognition pipeline no longer writes — it is EMPTY in every
// environment (0 rows). That is why the playground used to open with no
// scale and the measure tools stayed greyed out. It is kept only as a
// fallback for any environment still holding rows there; delete it once
// that's confirmed impossible, not before.
//
// Mapped back onto DocumentScaleConfigResponse so the provider seam and the
// single caller stay unchanged. `id` is synthesised — the recognition row's
// own id isn't carried over the wire, and nothing reads this field.
export const getDocumentScaleConfig = async (
  documentId: string,
): Promise<DocumentScaleConfigResponse[] | null> => {
  try {
    const detected = await api.get<QcDrawingScalesResponse>(
      `/QcFindings/DrawingScales?AttachmentId=${documentId}`,
    )
    // Server orders by page, so [0] is the first sheet carrying a scale —
    // which is what the caller treats as the document's scale.
    const scales = detected?.scales ?? []
    if (scales.length > 0) {
      return scales.map((s) => ({
        id: `${documentId}-p${s.pageNumber}`,
        text: s.text,
        value: s.value,
        attachmentId: documentId,
      }))
    }
  } catch {
    console.error('Failed to load detected drawing scale')
  }

  const data = await api.get<DocumentScaleConfigResponse[]>(
    `/SitePlanJobs/Scales?AttachmentId=${documentId}`,
  )
  if (!data) return null
  return data
}

export async function getDocumentLayers(
  documentId: string,
): Promise<OverlaysResponse> {
  const qcFindings = await getAllQcCheckFindings(documentId)
  const layers = filterLayersWithZeroCoordinates(
    normalizeLayersFromQcFindings(documentId, qcFindings),
  )
  return { documentId, layers }
}

export async function getDocumentOverlays(
  documentId: string,
): Promise<OverlaysResponse> {
  return getDocumentLayers(documentId)
}

export async function getQcOverlays(
  documentId: string,
): Promise<QcOverlaysDto | null> {
  try {
    return await api.get<QcOverlaysDto>(
      `/SitePlanJobs/QcOverlays?AttachmentId=${documentId}`,
    )
  } catch {
    console.error('Failed to load qc overlays')
    return null
  }
}

export async function getDocumentComments(
  documentId: string,
): Promise<CommentsResponse> {
  const data = await api.get<BackendComment[]>(
    `/SitePlanJobs/Comments?AttachmentId=${documentId}`,
  )
  if (!data) return { documentId, comments: [] }
  const comments: BackendComment[] = data.filter(
    (comment) =>
      comment.commentType === 'QpointCoreComment' ||
      (comment.coordinates?.x != 0 && comment.coordinates?.y != 0),
  )
  const base = normalizeCommentsResponse(comments, documentId)
  const qcFindings = await getAllQcCheckFindings(documentId)
  const findingComments = qcFindingsToComments(qcFindings, documentId)
  return { ...base, comments: [...base.comments, ...findingComments] }
}

function qcFindingsToComments(
  findings: BackendQcCheckFindingDto[],
  documentId: string,
): Comment[] {
  const result: Comment[] = []
  findings.forEach((finding) => {
    if (finding.isImplicitAiComment) return
    const accent = AI_STATUS_ACCENT[normalizeAiStatus(finding.status)]
    const bboxes = finding.evidenceBboxes ?? []
    bboxes.forEach((b, index) => {
      const pageIndex = b.page != null && b.page >= 1 ? b.page - 1 : 0
      const pageSize = usePdfDocStore
        .getState()
        .getPageSize(documentId, pageIndex)
      const norm = normalizeQcBbox(b, pageSize)
      if (!norm) return
      const text = [finding.name, finding.keyFinding]
        .filter((s): s is string => Boolean(s && s.trim()))
        .join(' — ')
      result.push({
        id: `qc-comment-${finding.id ?? `${documentId}-${index}`}-${index}`,
        documentId,
        pageIndex,
        xNorm: norm.x + norm.w / 2,
        yNorm: norm.y + norm.h / 2,
        status: 'open',
        text,
        createdAt: finding.createdDate ?? new Date().toISOString(),
        replyCount: finding.replyCount ?? 0,
        replies: [],
        isAiComment: true,
        commentType: CommentType.AIComment,
        replyTargetId: finding.id ?? '',
        accentColor: accent.accentColor,
        accentPillBg: accent.accentPillBg,
        accentPillText: accent.accentPillText,
      })
    })
  })
  return result
}

export { normalizeAiStatus } from './helpers/qc-status'

function toChecklistBboxes(
  raw: QcEvidenceBboxDto[] | undefined,
): AiChecklistEvidenceBbox[] {
  if (!raw) return []
  return raw
    .filter(
      (b): b is Required<QcEvidenceBboxDto> =>
        b.page != null &&
        b.x != null &&
        b.y != null &&
        b.w != null &&
        b.h != null,
    )
    .map((b) => ({ page: b.page, x: b.x, y: b.y, w: b.w, h: b.h }))
}

function groupQcFindingsByCategory(
  findings: BackendQcCheckFindingDto[],
): AiChecklistCategoryDto[] {
  const categoryMap = new Map<string, AiChecklistCategoryDto['items']>()

  findings.forEach((f, index) => {
    const cat = f.category?.trim() || 'General'
    if (!categoryMap.has(cat)) categoryMap.set(cat, [])
    categoryMap.get(cat)!.push({
      id: index,
      findingId: f.id ?? '',
      checkId: f.checkId ?? '',
      checkOrigin: f.checkOrigin ?? null,
      checkOriginName: f.checkOriginName ?? null,
      name: f.name ?? f.checkOriginName ?? '',
      status: normalizeAiStatus(f.status),
      finding: f.keyFinding ?? '',
      foundValue: '',
      detail: f.details ?? '',
      severity: null,
      pageNum: f.pageNumbers?.[0] ?? null,
      sheetId: null,
      evidenceLayers: f.evidenceLayers ?? [],
      evidenceBboxes: toChecklistBboxes(f.evidenceBboxes),
      reviewerName: f.reviewerName ?? null,
      reviewedAt: f.reviewedAt ?? null,
    })
  })

  return Array.from(categoryMap.entries()).map(([category, items]) => ({
    category,
    items,
  }))
}

export async function getImplicitAIComments(
  attachmentId: string,
): Promise<AiChecklistCategoryDto[]> {
  const findings = await getAllQcCheckFindings(attachmentId)
  const implicit = findings.filter((f) => f.isImplicitAiComment)
  return groupQcFindingsByCategory(implicit)
}

async function fetchQcCheckFindings(
  attachmentId: string,
): Promise<BackendQcCheckFindingDto[]> {
  try {
    const data = await api.get<BackendQcCheckFindingDto[]>(
      `/SitePlanJobs/QcFindings?AttachmentId=${attachmentId}`,
    )
    return data ?? []
  } catch {
    console.error('Failed to load qc_api check findings')
    return []
  }
}

export function getQcCheckFindings(
  attachmentId: string,
): Promise<BackendQcCheckFindingDto[]> {
  if (!attachmentId) return Promise.resolve([])
  return queryClient.fetchQuery({
    queryKey: queryKeys.qcFindings(attachmentId),
    queryFn: () => fetchQcCheckFindings(attachmentId),
    staleTime: 30_000,
    gcTime: 30_000,
  })
}

async function fetchAllQcCheckFindings(
  attachmentId: string,
): Promise<BackendQcCheckFindingDto[]> {
  try {
    const data = await api.get<BackendQcCheckFindingDto[]>(
      `/SitePlanJobs/AllQcFindings?AttachmentId=${attachmentId}`,
    )
    return data ?? []
  } catch {
    console.error('Failed to load merged qc_api check findings')
    return []
  }
}

export function getAllQcCheckFindings(
  attachmentId: string,
): Promise<BackendQcCheckFindingDto[]> {
  if (!attachmentId) return Promise.resolve([])
  return queryClient.fetchQuery({
    queryKey: queryKeys.allQcFindings(attachmentId),
    queryFn: () => fetchAllQcCheckFindings(attachmentId),
    staleTime: 30_000,
    gcTime: 30_000,
  })
}

export async function getCommentReplies(
  commentId: string,
): Promise<CommentReply[]> {
  const response = await api.get<Replies>(
    `/CommentReplies/comments/${commentId}/replies?skip=0&take=50`,
  )
  return response.replies
}
export async function addCommentReply(
  commentReply: CommentReplyRequest,
): Promise<void> {
  await api.post(`/CommentReplies/replies`, commentReply)
}

export async function addCityClerkComment(
  payload: CityClerkCommentRequest,
): Promise<void> {
  await api.post(`/SitePlanJobs/CityClerkComment`, payload)
}

export async function getDocumentMeasurements(
  documentId: string,
): Promise<MeasurementExportData | null> {
  const data: GetMeasurementsApiResponse[] = []
  return normalizeMeasurementExportData(data[0], documentId)
}

export async function getSitePlanKeynotesMatching(
  attachmentId: string,
): Promise<KeynotesMatchingResponse> {
  try {
    const data = await api.get<KeynotesMatchingResponse>(
      `/SitePlanJobs/KeynotesMatching?AttachmentId=${attachmentId}`,
    )
    const apiShape = backendKeynotesV2ToApiResponse(data, attachmentId)
    return normalizeKeynotesMatchingResponse(apiShape, attachmentId)
  } catch {
    console.error('Failed to load site plan keynotes matching')
    return {
      attachmentId,
      referenceKeynotes: [],
      planKeynotes: [],
      referenceKeynotesMissingInPlan: [],
      planKeynotesMissingInReference: [],
    }
  }
}
